<script setup lang="ts">
import Button from '@ui/components/forms/button.vue';
import Icon from '@ui/components/icon.vue';
import { computed } from 'vue';
import { useHelp } from './useHelp';

const {
    news,
    articles,
    hasNews,
    hasArticles,
    openNews,
    openArticle,
    isNewsRead,
    helpData,
} = useHelp();

const texts = computed(() => helpData.value.texts);
</script>

<template>
    <section class="customScrollbar flex flex-1 flex-col gap-4 overflow-y-auto p-2">
        <div
            v-if="hasNews"
            class="bg-brand-50 ring-brand-100 -mx-2 -mt-2 space-y-2 p-4 ring-1"
        >
            <h2 class="font-brand text-brand-600 m-0 text-[16px] font-bold">
                {{ texts.menu.whatsNewTitle }}
            </h2>
            <ul class="@[512px]/help:grid-cols-2 m-0  grid list-none auto-rows-fr gap-2 p-0">
                <li
                    v-for="news in news"
                    :key="news.id"
                    :class="[
                        'rounded bg-white ring-1  px-2 text-base w-full grid items-stretch ',
                        isNewsRead(news.id) ? 'ring-black/5 hover:ring-ring-brand-200 opacity-70 hover:opacity-100' : 'ring-brand-100 hover:ring-brand-200'
                    ].join(' ')"
                >
                    <button
                        class="relative flex w-full cursor-pointer flex-nowrap items-center gap-2 text-left font-sans text-base focus:outline-none focus:ring-0"
                        @click="openNews(news)"
                    >
                        <div class="flex grow flex-col items-start py-3">
                            <div
                                class="font-medium tracking-tight"
                                :class="[isNewsRead(news.id) ? 'text-gray-400' : 'text-gray-900 '].join(' ')"
                            >
                                {{ news.text }}
                            </div>
                        </div>
                        <span
                            class="text-brand-500 shrink-0"
                            :class="[isNewsRead(news.id) ? 'text-gray-300' : 'text-brand-500']"
                        >
                            <Icon :icon="isNewsRead(news.id) ? 'far fa-check-double' : 'far fa-chevron-right'"></Icon>
                        </span>
                    </button>
                </li>
            </ul>
        </div>
        <div
            v-if="hasArticles"
            class="px-2 pt-1"
        >
            <h2 class="font-brand m-0 text-[16px] font-bold text-gray-900">
                {{ texts.menu.articlesTitle }}
            </h2>
            <ul class="@[512px]/help:grid @[512px]/help:grid-cols-2 @[512px]/help:auto-rows-fr @[512px]/help:items-stretch m-0 mt-2 flex list-none flex-col gap-2 p-0">
                <li
                    v-for="article in articles"
                    :key="article.id"
                    class="grid w-full"
                >
                    <button
                        type="button"
                        class="hover:ring-brand-100 hover:bg-brand-50/50 flex w-full cursor-pointer items-center gap-2 rounded bg-white p-1 text-left font-sans text-base font-medium text-gray-900 ring-1 ring-black/5"
                        @click="openArticle(article)"
                    >
                        <span class="text-brand-500 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-sm">
                            <Icon icon="far fa-book"></Icon>
                        </span>
                        <span class="flex-1 tracking-tight">
                            {{ article.title }}
                        </span>
                        <span class="text-brand-500">
                            <Icon icon="far fa-chevron-right"></Icon>
                        </span>
                    </button>
                </li>
                <li class="col-span-full w-full">
                    <a
                        :href="texts.common.helpBaseUrl"
                        target="_blank"
                        class="hover:ring-brand-100 hover:bg-brand-50/50 flex w-full cursor-pointer items-center gap-2 rounded bg-white p-1 text-left font-sans text-sm text-gray-500 no-underline  ring-1 ring-black/5"
                    >
                        <span class="text-brand-500 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-sm">
                            <Icon icon="far fa-question-circle"></Icon>
                        </span>
                        <span class="flex-1 tracking-tight">
                            {{ texts.menu.moreArticlesText }} <span class="text-brand-500 font-medium underline">{{ texts.common.moreArticlesLink }}</span>
                        </span>
                        <span class="text-brand-500">
                            <Icon icon="far fa-chevron-right"></Icon>
                        </span>
                    </a>
                </li>
            </ul>
        </div>
        <div
            v-else
            class="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-base text-gray-900"
        >
            <div class="space-y-4">
                <div class="text-brand-500 flex items-center justify-center">
                    <Icon
                        icon="far fa-question-circle"
                        size="xlarge"
                    ></Icon>
                </div>
                <div>
                    {{ texts.menu.noArticlesText }}
                    <a
                        :href="texts.common.helpBaseUrl"
                        target="_blank"
                        class="text-brand-500 font-medium underline hover:no-underline"
                    >{{ texts.common.moreArticlesLink }}</a>.
                </div>
            </div>
        </div>
    </section>
</template>
